import React, { MouseEventHandler, PropsWithChildren, useReducer } from 'react';
import colors from '../../../helpers/colors';
import componentProps, { ComponentProps } from '../../../helpers/componentProps';
import { useIsPrinting } from '../../../hooks/usePrinting';
import { applyComponentTheme } from '../../../theme';
import Collapsible from '../../Collapsible';
import { InnerSection, SectionButton, StyledIcon, Title } from './styles';

type InnerAccordionSectionProps = {
  sectionId: string;
  entryId: string;
  title: string;
  initiallyDisplayEntry: boolean;
  forceOpenForPrint?: boolean;
  onExpand?: MouseEventHandler;
  onCollapse?: MouseEventHandler;
} & ComponentProps;

const InnerAccordionSection: React.FC<PropsWithChildren<InnerAccordionSectionProps>> = ({
  sectionId,
  entryId,
  title,
  initiallyDisplayEntry,
  forceOpenForPrint,
  onExpand,
  onCollapse,
  children,
  ...props
}) => {
  const isPrinting = useIsPrinting();

  const [displayEntryState, toggleDisplayEntryState] = useReducer(
    (state) => !state,
    initiallyDisplayEntry
  );

  const displayForPrinting = forceOpenForPrint && isPrinting;
  const displayEntry = !!(displayEntryState || displayForPrinting);

  const onClickWithCallback: MouseEventHandler = (event) => {
    if (onExpand && !displayEntry) {
      onExpand(event);
    }
    if (onCollapse && displayEntry) {
      onCollapse(event);
    }
    if (!displayForPrinting) {
      toggleDisplayEntryState();
    }
  };

  return (
    <InnerSection {...componentProps(props)}>
      <SectionButton
        id={sectionId}
        onClick={onClickWithCallback}
        aria-expanded={displayEntry}
        aria-controls={entryId}
        type="button">
        <Title>{title}</Title>
        <StyledIcon
          name="arrow-down"
          size="small"
          color={colors.core01}
          flipY={displayEntry}
        />
      </SectionButton>
      <Collapsible
        id={entryId}
        isOpened={displayEntry}
        role="region"
        aria-labelledby={sectionId}>
        {children}
      </Collapsible>
    </InnerSection>
  );
};

export default applyComponentTheme(InnerAccordionSection, 'InnerAccordionSection');
